import { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'

import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { useSubmitForm } from '../FormComponent/useSubmitForm'

import { BankReconTable } from './BankReconTable'
import {
  BankReconAmountEditPayload,
  BankReconDataSchema,
  BankReconTableData,
  BankReconTableUISchema,
  BankReconUISchema,
} from './types'

export function BankReconContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: BankReconUISchema
  dataSchema: BankReconDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title, description, emphasizeHeader, tables: tableUiSchemas = [], amountFormat, submitHandlerId } = uiSchema

  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitAmountEdit, isPending: isSubmitting } = useSubmitForm(
    submitHandlerId ?? '',
    tileFilterSelections,
    'bank-recon-amount-edit'
  )

  const [pendingEditRef, setPendingEditRef] = useState<string | null>(null)
  const [localAmountOverrides, setLocalAmountOverrides] = useState<Record<string, number>>({})

  const tableDataById = useMemo(() => {
    const map = new Map(dataSchema.tables.map((table) => [table.id, table]))
    return map
  }, [dataSchema.tables])

  const getRowAmountKey = (tableId: string, ref: string) => `${tableId}:${ref}`

  const serverAmountByTable = useMemo(() => {
    const byTable: Record<string, Record<string, number>> = {}
    for (const table of dataSchema.tables) {
      byTable[table.id] = Object.fromEntries(table.rows.map((row) => [row.ref, row.amount]))
    }
    return byTable
  }, [dataSchema.tables])

  const getRowAmount = useCallback(
    (tableId: string, ref: string, serverAmount: number) => {
      const override = localAmountOverrides[getRowAmountKey(tableId, ref)]
      return override ?? serverAmount
    },
    [localAmountOverrides]
  )

  const mergedTableData = useMemo(() => {
    return dataSchema.tables.map((table) => ({
      ...table,
      rows: table.rows.map((row) => ({
        ...row,
        amount: getRowAmount(table.id, row.ref, row.amount),
      })),
    }))
  }, [dataSchema.tables, getRowAmount])

  const handleAmountEdit = useCallback(
    (payload: BankReconAmountEditPayload) => {
      if (!submitHandlerId) return

      const key = getRowAmountKey(payload.tableId, payload.ref)
      setPendingEditRef(payload.ref)
      setLocalAmountOverrides((prev) => ({ ...prev, [key]: payload.amount }))

      submitAmountEdit(payload as unknown as Record<string, unknown>, {
        onSettled: () => setPendingEditRef(null),
        onSuccess: (response) => {
          if (response.uiSchema?.success === false) {
            setLocalAmountOverrides((prev) => {
              const next = { ...prev }
              delete next[key]
              return next
            })
          }
        },
        onError: () => {
          setLocalAmountOverrides((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
          })
        },
      })
    },
    [submitHandlerId, submitAmountEdit]
  )

  const hasHeader = !!title || !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  const resolvedTables = useMemo(() => {
    return tableUiSchemas
      .map((tableUi) => {
        const tableData = mergedTableData.find((table) => table.id === tableUi.id) ?? tableDataById.get(tableUi.id)
        if (!tableData) return null
        return { tableUi, tableData }
      })
      .filter((entry): entry is { tableUi: BankReconTableUISchema; tableData: BankReconTableData } => entry !== null)
  }, [tableUiSchemas, mergedTableData, tableDataById])

  const renderInTabs = resolvedTables.length > 1 && resolvedTables.some(({ tableUi }) => tableUi.renderInTabs === true)

  if (tableUiSchemas.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            {tileFilters && <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>}
          </ComponentHeader>
        )}
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          No tables configured
        </div>
      </div>
    )
  }

  const renderTable = (
    tableUi: BankReconTableUISchema,
    tableData: BankReconTableData,
    { hideTitle = false }: { hideTitle?: boolean } = {}
  ) => (
    <BankReconTable
      key={tableUi.id}
      tableUi={tableUi}
      tableData={tableData}
      serverAmountByRef={serverAmountByTable[tableUi.id] ?? {}}
      amountFormat={amountFormat}
      submitHandlerId={submitHandlerId}
      onAmountEdit={handleAmountEdit}
      isSubmitting={isSubmitting}
      pendingEditRef={pendingEditRef}
      hideTitle={hideTitle}
    />
  )

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          {tileFilters && <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>}
        </ComponentHeader>
      )}
      <ScrollArea className={clsx({ 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <div className="space-y-8 px-4 pb-6">
          {renderInTabs ? (
            <Tabs defaultValue={resolvedTables[0]?.tableUi.id} className="w-full">
              <TabsList className="h-auto w-full flex-wrap justify-start">
                {resolvedTables.map(({ tableUi }) => (
                  <TabsTrigger
                    key={tableUi.id}
                    value={tableUi.id}
                    className="group [&[data-state=active]]:bg-background"
                  >
                    {/* Stacked labels: the invisible bold copy reserves the wider bold width so
                        the tab does not resize when it becomes active. */}
                    <span className="grid text-center">
                      <span aria-hidden className="invisible col-start-1 row-start-1 font-bold">
                        {tableUi.title}
                      </span>
                      <span className="col-start-1 row-start-1 group-data-[state=active]:font-bold">
                        {tableUi.title}
                      </span>
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {resolvedTables.map(({ tableUi, tableData }) => (
                <TabsContent key={tableUi.id} value={tableUi.id} className="mt-4">
                  {renderTable(tableUi, tableData, { hideTitle: true })}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            resolvedTables.map(({ tableUi, tableData }) => renderTable(tableUi, tableData))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
